/**
 * Reading the activity trail.
 *
 * No API route and no service_role: audit_logs' SELECT policy is
 * `is_workspace_admin(workspace_id) OR is_platform_master()`, so a master
 * already reads across every workspace as the ordinary `authenticated` role.
 * Adding a privileged endpoint would widen the attack surface to buy nothing.
 *
 * KEYSET PAGINATION, NOT `.range()`. Two reasons, and the second is the one
 * that bites:
 *
 *   1. An unpaged PostgREST read silently truncates at 1000 rows with HTTP 200
 *      and no error. This table only grows, so it will cross that line.
 *
 *   2. `created_at` is NOT unique — the 35 rows present when this was written
 *      already contained two timestamp collisions, and a trigger writing
 *      several rows in one transaction stamps them identically. Ordering or
 *      seeking on it alone drops and repeats rows at page boundaries. That
 *      exact bug has been found three times in this codebase on other tables;
 *      the tie-break on `id` is here so this is not the fourth.
 *
 * Keyset rather than offset because an audit trail is written to while it is
 * being read: with OFFSET, a row inserted during paging shifts everything down
 * and the reader sees a row twice or not at all.
 *
 * @module services/auditLogService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config/supabase";
import { logError } from "./logger";
import type { ServiceResult } from "@/types";

/** One recorded action, as the page renders it. */
export interface AuditEntry {
  id: string;
  createdAt: string;
  entityType: string;
  action: string;
  entityId: string;
  /** Null when the write had no resolvable actor — see log_audit_event. */
  actorEmail: string | null;
  actorId: string | null;
  /** Survives the workspace being deleted; workspaceId does not resolve then. */
  workspaceName: string | null;
  workspaceId: string | null;
  changes: Record<string, unknown> | null;
  /** Context that is not a change: e.g. DEC5's override / reject reason ({ reason }). */
  metadata: Record<string, unknown> | null;
}

/** The cursor is the full sort key, because created_at alone is not unique. */
export interface AuditCursor {
  createdAt: string;
  id: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Absent when this is the last page. */
  nextCursor: AuditCursor | null;
}

export interface AuditQuery {
  /** Page size. The UI's "load more" appends. */
  limit?: number;
  /** Start after this row. Omit for the first page. */
  after?: AuditCursor | null;
  /** Narrow to one kind of thing, e.g. 'platform_account'. */
  entityType?: string | null;
  /** Narrow to one workspace. Null shows every workspace (master view). */
  workspaceId?: string | null;
}

const DEFAULT_LIMIT = 50;

interface Row {
  id: string;
  created_at: string;
  entity_type: string;
  action: string;
  entity_id: string;
  actor_email: string | null;
  user_id: string | null;
  workspace_name: string | null;
  workspace_id: string | null;
  changes: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

const toEntry = (r: Row): AuditEntry => ({
  id: r.id,
  createdAt: r.created_at,
  entityType: r.entity_type,
  action: r.action,
  entityId: r.entity_id,
  actorEmail: r.actor_email,
  actorId: r.user_id,
  workspaceName: r.workspace_name,
  workspaceId: r.workspace_id,
  changes: r.changes,
  metadata: r.metadata ?? null,
});

/**
 * Fetch one page of the trail, newest first.
 *
 * @param query - page size, cursor and optional filters
 * @returns the page, or a failure. NEVER a partial page reported as success:
 *   a truncated audit trail that looks complete is the thing this page exists
 *   to avoid, so a failed read is an error, not an empty list.
 */
export const fetchAuditPage = async (
  query: AuditQuery = {},
): Promise<ServiceResult<AuditPage>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Not available in demo mode" };
  }

  const limit = query.limit ?? DEFAULT_LIMIT;

  try {
    const supabase = getSupabaseClient();
    let q = supabase
      .from("audit_logs")
      .select(
        "id, created_at, entity_type, action, entity_id, actor_email, user_id, workspace_name, workspace_id, changes, metadata",
      )
      .order("created_at", { ascending: false })
      // The tie-break. Without it two rows sharing a timestamp can swap places
      // between requests, and the cursor below then skips or repeats them.
      .order("id", { ascending: false })
      // One extra row, purely to answer "is there another page?" without a
      // second round trip or a count. It is dropped before returning.
      .limit(limit + 1);

    if (query.entityType) q = q.eq("entity_type", query.entityType);
    if (query.workspaceId) q = q.eq("workspace_id", query.workspaceId);

    if (query.after) {
      // Strictly "older than the cursor row" in the same order as the sort:
      // an earlier timestamp, or the same timestamp with a smaller id.
      q = q.or(
        `created_at.lt.${query.after.createdAt},and(created_at.eq.${query.after.createdAt},id.lt.${query.after.id})`,
      );
    }

    const { data, error } = await q;
    if (error) {
      logError("AuditLog", "Failed to read audit page", error);
      return { success: false, error: error.message };
    }

    const rows = (data ?? []) as Row[];
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;
    const last = page[page.length - 1];

    return {
      success: true,
      data: {
        entries: page.map(toEntry),
        nextCursor: hasMore && last ? { createdAt: last.created_at, id: last.id } : null,
      },
    };
  } catch (err) {
    logError("AuditLog", "Audit page error", err);
    return { success: false, error: "An unexpected error occurred" };
  }
};

export default fetchAuditPage;
